import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  fetchCompareCars,
  type CompareCarItem,
  type CompareFeatureCategoryData,
  type CompareRowData,
} from "@/data/carsData";
import {
  type LucideIcon,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CarFront,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Fuel,
  Gauge,
  Hash,
  Monitor,
  Palette,
  Settings2,
  Shield,
  ShieldCheck,
  Sparkles,
  Star,
  Sun,
  TrendingUp,
  Users,
  Volume2,
  WalletCards,
  Wrench,
  XCircle,
} from "lucide-react";
import "./CompareCars.css";

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );

    const observeElements = () => {
      document.querySelectorAll(".scroll-reveal").forEach((el) => {
        if (!el.classList.contains("revealed")) {
          observer.observe(el);
        }
      });
    };

    observeElements();

    const mutationObserver = new MutationObserver(() => {
      observeElements();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
}

type CarItem = CompareCarItem;

const getCarTitle = (car: CarItem) => car.title || "";

const getCarShortTitle = (car: CarItem) => car.title || "";

const formatPrice = (value: number | null) =>
  value === null
    ? ""
    : `$${value.toLocaleString("en-CA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;

const formatWeekly = (value: number | null) =>
  value === null
    ? ""
    : `$${value.toLocaleString("en-CA", {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
      })}/week`;

const formatNumber = (value: number | null) =>
  value === null ? "" : value.toLocaleString("en-CA");

const normalizeFeatureName = (value: string) => value.trim().toLowerCase();

const getFeatureCategoryKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function getFeatureCategoryIcon(category: string): LucideIcon {
  switch (category.trim().toLowerCase()) {
    case "mechanical":
      return Wrench;
    case "exterior":
      return Sun;
    case "entertainment":
      return Volume2;
    case "interior":
      return Monitor;
    case "safety":
      return Shield;
    case "comfort":
      return Star;
    default:
      return Sparkles;
  }
}

type DisplayCompareRow = CompareRowData & {
  icon: LucideIcon;
};

type MoreFeatureGroup = {
  key: string;
  title: string;
  icon: LucideIcon;
  betterItemId: string | null;
  countsByCar: number[];
  features: Array<{
    key: string;
    name: string;
    availability: boolean[];
  }>;
};

export default function CompareCars() {
  useScrollReveal();
  const [searchParams] = useSearchParams();

  const selectedIds = useMemo(
    () =>
      Array.from(
        new Set(
          (searchParams.get("cars") || "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean),
        ),
      ).slice(0, 2),
    [searchParams],
  );

  const [comparedCars, setComparedCars] = useState<CarItem[]>([]);
  const [comparisonRowsFromApi, setComparisonRowsFromApi] = useState<
    CompareRowData[]
  >([]);
  const [loading, setLoading] = useState(selectedIds.length >= 2);
  const [error, setError] = useState("");

  const [featureCategoryComparison, setFeatureCategoryComparison] = useState<
    CompareFeatureCategoryData[]
  >([]);
  const [openFeatureCategory, setOpenFeatureCategory] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let isMounted = true;

    if (selectedIds.length < 2) {
      setComparedCars([]);
      setComparisonRowsFromApi([]);
      setFeatureCategoryComparison([]);
      setOpenFeatureCategory(null);
      setError("");
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const loadComparison = async () => {
      try {
        setLoading(true);
        setError("");

        const result = await fetchCompareCars(selectedIds);

        if (!isMounted) return;

        setComparedCars(result.items);
        setComparisonRowsFromApi(result.comparison);
        setFeatureCategoryComparison(result.featureCategoryComparison);
      } catch (err) {
        if (!isMounted) return;

        setComparedCars([]);
        setComparisonRowsFromApi([]);
        setFeatureCategoryComparison([]);
        setOpenFeatureCategory(null);
        setError(
          err instanceof Error ? err.message : "Unable to load comparison.",
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadComparison();

    return () => {
      isMounted = false;
    };
  }, [selectedIds]);

  useEffect(() => {
    const availableKeys = featureCategoryComparison
      .map((group) => getFeatureCategoryKey(group.category))
      .filter(Boolean);

    if (availableKeys.length === 0) {
      setOpenFeatureCategory(null);
      return;
    }

    setOpenFeatureCategory((prev) =>
      prev && availableKeys.includes(prev) ? prev : availableKeys[0],
    );
  }, [featureCategoryComparison]);

  const rowIcons: Record<string, LucideIcon> = {
    price: CircleDollarSign,
    weekly_price: WalletCards,
    price_tag: BadgeCheck,
    condition: ShieldCheck,
    kilometres: Gauge,
    body_style: CarFront,
    engine: Sparkles,
    transmission: Settings2,
    fuel_type: Fuel,
    exterior_colour: Palette,
    drive_train: TrendingUp,
    passengers: Users,
    cylinders: Hash,
  };

  const comparisonRows = useMemo<DisplayCompareRow[]>(() => {
    const mapped = comparisonRowsFromApi.map((row) => ({
      ...row,
      icon: rowIcons[row.key] ?? BadgeCheck,
    }));

    return mapped;
  }, [comparisonRowsFromApi, comparedCars]);

  const moreFeatureGroups = useMemo<MoreFeatureGroup[]>(() => {
    return featureCategoryComparison
      .map((categoryRow) => {
        const title = categoryRow.category.trim() || "Other Features";
        const key = getFeatureCategoryKey(title);

        const featuresByCarId = new Map(
          categoryRow.values.map((value) => [
            value.itemId,
            new Set(
              value.features
                .map((feature) => normalizeFeatureName(feature.name))
                .filter(Boolean),
            ),
          ]),
        );

        const distinctFeatures = new Map<string, string>();

        categoryRow.values.forEach((value) => {
          value.features.forEach((feature) => {
            const normalizedName = normalizeFeatureName(feature.name);
            const displayName = feature.name.trim();

            if (
              normalizedName &&
              displayName &&
              !distinctFeatures.has(normalizedName)
            ) {
              distinctFeatures.set(normalizedName, displayName);
            }
          });
        });

        const features = Array.from(distinctFeatures.entries())
          .map(([normalizedName, displayName]) => ({
            key: `${key}-${normalizedName}`,
            name: displayName,
            availability: comparedCars.map(
              (car) =>
                featuresByCarId.get(car.id)?.has(normalizedName) ?? false,
            ),
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        const countsByCar = comparedCars.map((car) => {
          const matched = categoryRow.values.find(
            (value) => value.itemId === car.id,
          );
          return matched?.count ?? matched?.features.length ?? 0;
        });

        return {
          key,
          title,
          icon: getFeatureCategoryIcon(title),
          betterItemId: categoryRow.betterItemId,
          countsByCar,
          features,
        };
      })
      .filter((group) => group.features.length > 0);
  }, [featureCategoryComparison, comparedCars]);

  const winnerForRow = (rowKey: string) =>
    comparisonRows.find((row) => row.key === rowKey)?.betterItemId ?? null;

  const betterCarFor = (rowKey: string) => {
    const betterId = winnerForRow(rowKey);
    if (!betterId) return null;
    return comparedCars.find((car) => car.id === betterId) ?? null;
  };

  const lowerPriceCar = betterCarFor("price");
  const lowerKmCar = betterCarFor("kilometres");
  const lowerWeeklyCar = betterCarFor("weekly_price");

  const formatRowValue = (
    rowKey: string,
    value: string | number | null | undefined,
  ) => {
    if (value === null || value === undefined || value === "") return "";

    if (rowKey === "price") {
      return formatPrice(Number(value));
    }

    if (rowKey === "weekly_price") {
      return formatWeekly(Number(value));
    }

    if (rowKey === "kilometres") {
      return Number(value).toLocaleString("en-CA");
    }

    return String(value);
  };

  if (loading) {
    return (
      <main className="compare-page compare-page--empty compare-page--with-navbar-offset">
        <section className="compare-empty-state">
          <div className="compare-empty-state__inner">
            <span className="compare-empty-state__eyebrow">
              Compare Vehicles
            </span>
            <h1>Loading comparison...</h1>
            <p>Please wait while we fetch the selected vehicles.</p>

            <div className="compare-empty-state__actions">
              <Link to="/car-inventory" className="compare-primary-btn">
                <ArrowLeft className="w-4 h-4" />
                Back to Inventory
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="compare-page compare-page--empty compare-page--with-navbar-offset">
        <section className="compare-empty-state">
          <div className="compare-empty-state__inner">
            <span className="compare-empty-state__eyebrow">
              Compare Vehicles
            </span>
            <h1>Unable to load comparison.</h1>
            <p>{error}</p>

            <div className="compare-empty-state__actions">
              <Link to="/car-inventory" className="compare-primary-btn">
                <ArrowLeft className="w-4 h-4" />
                Back to Inventory
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }
  if (comparedCars.length < 2) {
    return (
      <main className="compare-page compare-page--empty compare-page--with-navbar-offset">
        <section className="compare-empty-state">
          <div className="compare-empty-state__inner">
            <span className="compare-empty-state__eyebrow">
              Compare Vehicles
            </span>
            <h1>Choose any 2 vehicles to unlock the premium compare view.</h1>
            <p>
              Select two vehicles from inventory and click the compare button to
              view a stunning side-by-side comparison.
            </p>

            <div className="compare-empty-state__actions">
              <Link to="/car-inventory" className="compare-primary-btn">
                <ArrowLeft className="w-4 h-4" />
                Back to Inventory
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="compare-page compare-page--with-navbar-offset">
      {/* Hero */}
      <section className="compare-hero">
        <div className="compare-hero__overlay" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 compare-hero__container">
          <div className="compare-hero__grid">
            <div className="compare-hero__content scroll-reveal">
              <span className="compare-hero__eyebrow">
                Precision Vehicle Comparison
              </span>

              <h1 className="compare-hero__title">
                Compare Your <span>Next Ride</span> With Confidence
              </h1>

              <p className="compare-hero__text">
                A premium side-by-side look at your shortlisted vehicles —
                pricing, value, specs, and details laid out beautifully so your
                decision feels easy and clear.
              </p>

              <div className="compare-hero__cta">
                <Link to="/car-inventory" className="compare-primary-btn">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Inventory
                </Link>

                {/* <a href="#compare-table" className="compare-secondary-btn">
                  View Comparison
                  <ArrowRight className="w-4 h-4" />
                </a> */}
              </div>

              <div className="compare-hero__pills">
                <div className="compare-pill">
                  <span className="compare-pill__label">Lower Price</span>
                  <strong>
                    {lowerPriceCar ? getCarShortTitle(lowerPriceCar) : "-"}
                  </strong>
                </div>

                <div className="compare-pill">
                  <span className="compare-pill__label">Lower Weekly</span>
                  <strong>
                    {lowerWeeklyCar ? getCarShortTitle(lowerWeeklyCar) : "-"}
                  </strong>
                </div>

                <div className="compare-pill">
                  <span className="compare-pill__label">Lower KM</span>
                  <strong>
                    {lowerKmCar ? getCarShortTitle(lowerKmCar) : "-"}
                  </strong>
                </div>
              </div>
            </div>

            <div className="compare-hero__cars scroll-reveal">
              {comparedCars.map((car, index) => (
                <article key={car.id} className="compare-hero-card">
                  <div className="compare-hero-card__image-wrap">
                    {car.image ? (
                      <img
                        src={car.image}
                        alt={getCarTitle(car)}
                        className="compare-hero-card__image"
                      />
                    ) : (
                      <div className="compare-hero-card__image" />
                    )}
                    <span className="compare-hero-card__badge">
                      Vehicle {index + 1}
                    </span>
                  </div>

                  <div className="compare-hero-card__body">
                    <h3>{getCarTitle(car)}</h3>

                    <div className="compare-hero-card__pricing">
                      <div>
                        <span>Price</span>
                        <strong>{formatPrice(car.price)}</strong>
                      </div>
                      <div>
                        <span>Weekly</span>
                        <strong>
                          {car.weeklyPayment === null
                            ? ""
                            : `$${car.weeklyPayment.toLocaleString("en-CA", {
                                minimumFractionDigits: Number.isInteger(
                                  car.weeklyPayment,
                                )
                                  ? 0
                                  : 2,
                                maximumFractionDigits: 2,
                              })}`}
                        </strong>
                      </div>
                    </div>

                    <div className="compare-hero-card__specs">
                      <span>{car.bodyStyle || ""}</span>
                      <span>{car.transmission || ""}</span>
                      <span>
                        {car.km !== null ? `${formatNumber(car.km)} km` : ""}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Selected vehicles strip */}
      {/* <section className="compare-selected scroll-reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="compare-selected__box">
            <div className="compare-selected__head">
              <span className="compare-section-eyebrow">Selected Vehicles</span>
              <h2>Two vehicles. One crystal-clear decision.</h2>
            </div>

            <div className="compare-selected__grid">
              {comparedCars.map((car) => (
                <div key={car.id} className="compare-selected-card">
                  {car.image ? (
                    <img src={car.image} alt={getCarTitle(car)} />
                  ) : (
                    <div />
                  )}
                  <div>
                    <h3>{getCarTitle(car)}</h3>
                    <p>
                      {[formatPrice(car.price), formatWeekly(car.weeklyPayment)]
                        .filter(Boolean)
                        .join(" • ")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Comparison table */}
      <section id="compare-table" className="compare-table-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="compare-table-wrapper scroll-reveal">
            <div className="compare-table-intro">
              <h2>Detailed Vehicle Comparison</h2>
              <p>
                Every major detail — from price to drivetrain, transmission,
                condition, and more.
              </p>
            </div>

            <div className="compare-table">
              <div className="compare-table__row compare-table__row--header">
                <div className="compare-table__cell compare-table__cell--feature compare-table__cell--feature-header">
                  <span>Features</span>
                </div>

                {comparedCars.map((car) => (
                  <div
                    key={car.id}
                    className="compare-table__cell compare-table__cell--car-header"
                  >
                    <div className="compare-table__car-top">
                      {car.image ? (
                        <img src={car.image} alt={getCarTitle(car)} />
                      ) : (
                        <div />
                      )}
                      <div>
                        <p className="compare-table__car-label">Selected Car</p>
                        <h3>{getCarTitle(car)}</h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {comparisonRows.map((row) => {
                const Icon = row.icon;
                const winnerId = winnerForRow(row.key);

                return (
                  <div key={row.key} className="compare-table__row">
                    <div className="compare-table__cell compare-table__cell--feature">
                      <div className="compare-feature">
                        <span className="compare-feature__icon">
                          <Icon className="w-4 h-4" />
                        </span>
                        <span className="compare-feature__text">
                          {row.label}
                        </span>
                      </div>
                    </div>

                    {comparedCars.map((car, index) => (
                      <div
                        key={`${row.key}-${car.id}`}
                        className={`compare-table__cell compare-table__value ${
                          winnerId === car.id ? "is-better" : ""
                        } ${
                          row.key === "price_tag" && car.isGoodPrice
                            ? "is-good-price"
                            : ""
                        }`}
                      >
                        <span>
                          {formatRowValue(row.key, row.values[index])}
                        </span>

                        {winnerId === car.id && (
                          <em className="compare-table__winner">Better</em>
                        )}

                        {row.key === "price_tag" && car.isGoodPrice && (
                          <em className="compare-table__winner">Recommended</em>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}

              {moreFeatureGroups.length > 0 && (
                <>
                  <div className="compare-table__row compare-table__row--section-heading">
                    <div className="compare-table__cell compare-table__cell--feature compare-table__cell--feature-section-title">
                      <span>More Features</span>
                    </div>

                    {comparedCars.map((car) => (
                      <div
                        key={`more-features-heading-${car.id}`}
                        className="compare-table__cell compare-table__cell--section-blank"
                      />
                    ))}
                  </div>

                  {moreFeatureGroups.map((group) => {
                    const Icon = group.icon;
                    const isOpen = openFeatureCategory === group.key;

                    return (
                      <div key={group.key}>
                        <div
                          className={`compare-table__row compare-table__row--feature-category ${
                            isOpen ? "is-open" : ""
                          }`}
                        >
                          <div className="compare-table__cell compare-table__cell--feature">
                            <button
                              type="button"
                              className="compare-feature-accordion-trigger"
                              onClick={() =>
                                setOpenFeatureCategory((prev) =>
                                  prev === group.key ? null : group.key,
                                )
                              }
                              aria-expanded={isOpen}
                            >
                              <div className="compare-feature">
                                <span className="compare-feature__icon">
                                  <Icon className="w-4 h-4" />
                                </span>
                                <span className="compare-feature__text">
                                  {group.title}
                                </span>
                              </div>

                              <span className="compare-feature-accordion__meta">
                                <span className="compare-feature-accordion__chevron">
                                  {isOpen ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </span>
                              </span>
                            </button>
                          </div>

                          {comparedCars.map((car, index) => (
                            <div
                              key={`${group.key}-${car.id}`}
                              className={`compare-table__cell compare-table__value compare-table__value--category ${
                                group.betterItemId === car.id ? "is-better" : ""
                              }`}
                            >
                              <span className="compare-feature-category-summary">
                                <span className="compare-feature-category-summary__count">
                                  <strong>{group.countsByCar[index]}</strong>{" "}
                                  {group.countsByCar[index] === 1
                                    ? "feature"
                                    : "features"}
                                </span>

                                {group.betterItemId === car.id && (
                                  <em className="compare-table__winner">
                                    More
                                  </em>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>

                        {isOpen &&
                          group.features.map((feature) => (
                            <div
                              key={feature.key}
                              className="compare-table__row compare-table__row--feature-detail"
                            >
                              <div className="compare-table__cell compare-table__cell--feature">
                                <div className="compare-feature compare-feature--subitem compare-feature--subitem-no-icon">
                                  <span className="compare-feature__subtext">
                                    {feature.name}
                                  </span>
                                </div>
                              </div>

                              {comparedCars.map((car, carIndex) => {
                                const hasFeature =
                                  feature.availability[carIndex];

                                return (
                                  <div
                                    key={`${feature.key}-${car.id}`}
                                    className="compare-table__cell compare-table__value compare-table__value--feature-availability"
                                  >
                                    <span
                                      className={`compare-feature-status ${
                                        hasFeature
                                          ? "compare-feature-status--yes"
                                          : "compare-feature-status--no"
                                      }`}
                                      aria-label={
                                        hasFeature
                                          ? "Available"
                                          : "Not Available"
                                      }
                                      title={
                                        hasFeature
                                          ? "Available"
                                          : "Not Available"
                                      }
                                    >
                                      {hasFeature ? (
                                        <CheckCircle2 className="w-5 h-5" />
                                      ) : (
                                        <XCircle className="w-5 h-5" />
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="compare-cta-section scroll-reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="compare-cta-box">
            <div>
              <span className="compare-section-eyebrow">Next Step</span>
              <h2>Found the better fit?</h2>
              <p>
                Explore financing, contact the dealership, or head back to
                inventory to compare another pair.
              </p>
            </div>

            <div className="compare-cta-box__actions">
              <Link to="/#financing" className="compare-primary-btn">
                Explore Financing
                <ArrowRight className="w-4 h-4" />
              </Link>

              <Link
                to="/contact"
                className="compare-secondary-btn compare-secondary-btn--light"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
